import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";

const PulseLine = ({ flip = false }) => (
  <svg viewBox="0 0 400 60" preserveAspectRatio="none" className={`h-10 sm:h-14 w-full ${flip ? "-scale-x-100" : ""}`} aria-hidden="true">
    <defs>
      <linearGradient id={`pl-${flip ? "r" : "l"}`} x1="0" x2="1">
        <stop offset="0" stopColor={flip ? "#00F0FF" : "#FF2A55"} stopOpacity="0" />
        <stop offset="0.55" stopColor={flip ? "#00F0FF" : "#FF2A55"} stopOpacity="0.9" />
        <stop offset="1" stopColor="#ffffff" stopOpacity="1" />
      </linearGradient>
    </defs>
    <polyline
      points="0,30 250,30 268,30 280,8 292,52 304,18 314,36 326,30 400,30"
      fill="none"
      stroke={`url(#pl-${flip ? "r" : "l"})`}
      strokeWidth="2.5"
      strokeLinejoin="round"
      strokeLinecap="round"
      style={{ filter: `drop-shadow(0 0 6px ${flip ? "rgba(0,240,255,0.9)" : "rgba(255,42,85,0.9)"})` }}
    />
  </svg>
);

const particles = Array.from({ length: 18 }, (_, i) => ({
  left: `${(i * 53) % 100}%`,
  top: `${(i * 37 + 11) % 90}%`,
  size: 2 + (i % 3),
  color: i % 2 ? "#00F0FF" : "#FF2A55",
  delay: `${(i % 6) * 0.7}s`,
  dur: `${4 + (i % 4)}s`,
}));

export const BrandBanner = ({ full = false }) => (
  <section data-testid="brand-banner" className={full ? "w-screen h-screen" : "max-w-[1400px] mx-auto px-5 sm:px-8 pt-20"}>
    <motion.div
      initial={full ? false : { opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
      className={`relative overflow-hidden bg-black ${full ? "w-full h-full" : "aspect-[4/5] sm:aspect-[16/9] lg:aspect-[21/9] border border-white/[0.08] kp-card-clip"}`}
    >
      <img src="/kuropulse-banner.jpg" alt="" className="absolute inset-0 w-full h-full object-cover object-center" />
      <div className="absolute inset-0 kp-scanlines opacity-50" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_35%,rgba(0,0,0,0.75)_100%)]" />
      <div className="absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-black to-transparent" />
      <div className="absolute inset-0 pointer-events-none">
        {particles.map((p, i) => (
          <span
            key={i}
            className="absolute rounded-full kp-particle"
            style={{ left: p.left, top: p.top, width: p.size, height: p.size, background: p.color, boxShadow: `0 0 ${p.size * 3}px ${p.color}`, animationDelay: p.delay, animationDuration: p.dur }}
          />
        ))}
      </div>

      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
        <div className={`relative w-full flex flex-col items-center py-5 sm:py-8 lg:-translate-x-[3%] ${full ? "max-w-[44%]" : "max-w-[62%] sm:max-w-[52%]"}`}>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0.85)_0%,rgba(0,0,0,0.55)_55%,transparent_100%)]" />
          <div className="absolute inset-x-[-30%] top-1/2 -translate-y-1/2 h-px bg-gradient-to-r from-transparent via-[#FF2A55]/30 to-transparent" />
          <div className="relative flex items-center w-full gap-2 sm:gap-4">
            <PulseLine />
            <Logo size={full ? 72 : 44} className="shrink-0 drop-shadow-[0_0_18px_rgba(0,240,255,0.7)]" />
            <PulseLine flip />
          </div>
          <h2
            data-testid="brand-title"
            className={`relative font-display font-black uppercase leading-none tracking-[0.14em] mt-2 ${full ? "text-[5vw]" : "text-3xl sm:text-5xl lg:text-6xl xl:text-7xl"}`}
          >
            <span className="kp-neon-red">Kuro</span>
            <span className="kp-neon-cyan">Pulse</span>
          </h2>
          <p
            data-testid="brand-tagline"
            className={`relative mt-3 sm:mt-5 font-mono uppercase tracking-[0.32em] text-white ${full ? "text-[1.3vw]" : "text-xs sm:text-base lg:text-xl"}`}
            style={{ textShadow: "0 0 10px rgba(0,240,255,0.9), 0 0 24px rgba(0,240,255,0.5)" }}
          >
            Your Ultimate Anime Universe
          </p>
        </div>
      </div>
    </motion.div>
  </section>
);
